// A single gate detail card. Renders the full read on a gate-or-flight
// entity: plane info, drivers (FWD/AFT) with status, tug, bag download
// progress (per-bin breakdown), outbound connections with hot timers +
// confidence prediction, and what's up next at the gate.
//
// This component is a pure card — it takes the entity as a prop and
// reads context only for actions (pin, minimize, clear). The Workspace
// orchestrator decides which entities get rendered as cards.

import React, { useState } from 'react';
import { Box, Paper, Typography, IconButton, LinearProgress, Tooltip, Chip, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import FlightLandIcon from '@mui/icons-material/FlightLand';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import LuggageIcon from '@mui/icons-material/Luggage';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PersonIcon from '@mui/icons-material/Person';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import AddIcon from '@mui/icons-material/Add';
import { useIntel } from './IntelligenceContext';
import { GATE_DETAILS, DRIVER_STATUS_META, PHASE_LABELS } from './mockGateDetails';
import { GATE_STATE } from './mockLiveState';
import { useLiveGateDetail } from './useLiveDetail';
import { C } from '../styles';

const SEVERITY_BG = {
  red:  'rgba(255, 0, 0, 0.10)',
  gold: 'rgba(249, 182, 18, 0.10)',
};
const SEVERITY_COLOR = {
  red:  '#FF0000',
  gold: '#F9B612',
};

// Resolve an entity → gate detail. Returns null if not gate/flight or no match.
function resolveDetail(entity) {
  if (!entity) return null;
  if (entity.kind === 'gate')   return GATE_DETAILS.get(entity.id) || null;
  if (entity.kind === 'flight') {
    for (const d of GATE_DETAILS.values()) {
      if (d.flightNumber === entity.id) return d;
    }
  }
  return null;
}

function GateDetailCard({ entity, isSelected }) {
  const intel = useIntel();
  const {
    setSelected, clearSelection,
    isPinned, togglePin, toggleMinimize,
    annotationsFor, addAnnotation, removeAnnotation,
  } = intel;

  const staticDetail = resolveDetail(entity);
  const detail = useLiveGateDetail(staticDetail);
  const notes = annotationsFor(entity);
  const [draft, setDraft] = useState('');
  const [adding, setAdding] = useState(false);

  if (!detail) return null;
  const state = GATE_STATE.get(detail.gateId);
  const pinned = isPinned(entity);

  // Selected card → close with X clears selection.
  // Pinned-but-not-selected → close with X unpins.
  const onClose = () => {
    if (isSelected) clearSelection();
    else togglePin(entity);
  };

  return (
    <Paper
      data-intel-overlay
      elevation={0}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={() => !isSelected && setSelected(entity)}
      sx={{
        backgroundColor: 'rgba(30, 30, 30, 0.7)',
        backdropFilter: C.blur,
        WebkitBackdropFilter: C.blur,
        border: `1px solid ${isSelected ? C.accent : C.border}`,
        borderRadius: 1.5,
        boxShadow: isSelected
          ? `0 0 0 1px ${C.accent}, 0 8px 32px rgba(0, 0, 0, 0.45)`
          : '0 8px 32px rgba(0, 0, 0, 0.30)',
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
        flex: '1 1 auto',
        minHeight: 0,
        cursor: isSelected ? 'default' : 'pointer',
      }}
    >
      {/* Header */}
      <Box sx={{
        display: 'flex', alignItems: 'flex-start', gap: 1,
        px: 1.5, py: 1.25,
        borderBottom: `1px solid ${C.border}`,
        backgroundColor: 'rgba(0,0,0,0.3)',
        flexShrink: 0,
      }}>
        <Box sx={{
          width: 38, height: 38,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backgroundColor: state?.state === 'parked'   ? '#4CAF50'
                          : state?.state === 'arriving' ? '#2196F3'
                          : state?.state === 'pushing'  ? '#F6530F'
                          : '#333',
          color: '#fff',
          fontWeight: 700, fontSize: '0.95rem', letterSpacing: 0.4,
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          borderRadius: 0.6,
          flexShrink: 0,
        }}>
          {detail.gateNum}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{
            fontSize: '0.55rem', color: C.textMuted, fontWeight: 700,
            letterSpacing: 0.6, textTransform: 'uppercase',
          }}>
            Gate {detail.gateId} · {state?.state}
          </Typography>
          <Typography sx={{
            fontSize: '1rem', color: C.textPrimary, fontWeight: 700,
            lineHeight: 1.15, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          }}>
            {detail.flightNumber}
          </Typography>
          <Typography noWrap sx={{ fontSize: '0.65rem', color: C.textSecondary, lineHeight: 1.2 }}>
            From {detail.origin} · {detail.tail} · ETA {detail.eta || '—'}
          </Typography>
        </Box>
        {/* Header actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
          <Tooltip title={pinned ? 'Unpin' : 'Pin to keep open'} placement="bottom">
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); togglePin(entity); }}
              sx={{
                color: pinned ? C.accent : C.textMuted,
                '&:hover': { color: C.accent, backgroundColor: 'rgba(249, 182, 18, 0.08)' },
              }}
            >
              {pinned
                ? <PushPinIcon sx={{ fontSize: 16 }} />
                : <PushPinOutlinedIcon sx={{ fontSize: 16 }} />}
            </IconButton>
          </Tooltip>
          {pinned && (
            <Tooltip title="Minimize" placement="bottom">
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); toggleMinimize(entity); }}
                sx={{ color: C.textMuted, '&:hover': { color: C.textSecondary } }}
              >
                <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={isSelected ? 'Clear selection' : 'Close (unpin)'} placement="bottom">
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onClose(); }}
              sx={{ color: C.textMuted, '&:hover': { color: '#f44336' } }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Body — scrolls */}
      <Box sx={{
        flex: 1, overflowY: 'auto', minHeight: 0,
        '&::-webkit-scrollbar': { width: 6 },
        '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(255,255,255,0.12)', borderRadius: 3 },
      }}>
        {/* Issues block */}
        {detail.issues.length > 0 && (
          <Box sx={{ px: 1.5, py: 1.25, borderBottom: `1px solid ${C.border}` }}>
            <SectionLabel icon={WarningAmberIcon} color="#FF0000">
              Issues ({detail.issues.length})
            </SectionLabel>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {detail.issues.map((iss, i) => (
                <Box key={i} sx={{
                  display: 'flex', alignItems: 'center', gap: 0.75,
                  px: 0.85, py: 0.5,
                  backgroundColor: SEVERITY_BG[iss.severity],
                  borderLeft: `3px solid ${SEVERITY_COLOR[iss.severity]}`,
                  borderRadius: 0.4,
                }}>
                  <Box sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: SEVERITY_COLOR[iss.severity] }} />
                  <Typography sx={{ fontSize: '0.7rem', color: C.textPrimary, flex: 1 }}>
                    {iss.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Crew + Tug */}
        <Box sx={{ px: 1.5, py: 1.25, borderBottom: `1px solid ${C.border}` }}>
          <SectionLabel icon={PersonIcon} color={C.textSecondary}>Crew</SectionLabel>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
            {detail.drivers.map(d => {
              const meta = DRIVER_STATUS_META[d.status];
              return (
                <Box key={d.id}
                  onClick={(e) => { e.stopPropagation(); setSelected({ kind: 'driver', id: d.id }); }}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1,
                    px: 0.85, py: 0.6, borderRadius: 0.5,
                    cursor: 'pointer',
                    backgroundColor: 'rgba(255,255,255,0.025)',
                    border: `1px solid ${C.border}`,
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)', borderColor: meta.color },
                  }}
                >
                  <Box sx={{
                    width: 30, height: 22,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    backgroundColor: 'rgba(0,0,0,0.4)',
                    border: `1px solid ${meta.color}`, borderRadius: 0.4,
                    color: meta.color, fontWeight: 800, fontSize: '0.6rem', letterSpacing: 0.5,
                  }}>
                    {d.role}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography noWrap sx={{ fontSize: '0.78rem', color: C.textPrimary, fontWeight: 600, lineHeight: 1.1 }}>
                      {d.name}
                    </Typography>
                    <Typography sx={{ fontSize: '0.6rem', color: C.textMuted, lineHeight: 1.1 }}>
                      Badge {d.badge}
                    </Typography>
                  </Box>
                  <Chip size="small" label={meta.label.toUpperCase()}
                    sx={{
                      height: 18, fontSize: '0.55rem', fontWeight: 800, letterSpacing: 0.5,
                      color: meta.color, backgroundColor: `${meta.color}22`,
                      border: `1px solid ${meta.color}55`,
                      borderRadius: 0.4,
                      '& .MuiChip-label': { px: 0.6 },
                    }}
                  />
                </Box>
              );
            })}
          </Box>
          <Box sx={{
            mt: 0.75, display: 'flex', alignItems: 'center', gap: 1,
            px: 0.85, py: 0.5, borderRadius: 0.5,
            backgroundColor: 'rgba(255,255,255,0.025)',
            border: `1px solid ${detail.tug ? C.border : '#F9B61255'}`,
          }}>
            <LocalShippingIcon sx={{ fontSize: 16, color: detail.tug ? C.textSecondary : '#F9B612' }} />
            <Typography sx={{ fontSize: '0.75rem', color: C.textPrimary, fontWeight: 600, flex: 1 }}>
              {detail.tug || 'No tug assigned'}
            </Typography>
            {detail.tug && (
              <Typography sx={{ fontSize: '0.6rem', color: C.textMuted }}>Pre-positioned</Typography>
            )}
          </Box>
        </Box>

        {/* Download / bags */}
        <Box sx={{ px: 1.5, py: 1.25, borderBottom: `1px solid ${C.border}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
            <SectionLabel icon={LuggageIcon} color={C.textSecondary}>Download</SectionLabel>
            <Typography sx={{
              fontSize: '0.7rem', color: detail.bagPct < 100 ? C.accent : '#4CAF50',
              fontWeight: 700, fontVariantNumeric: 'tabular-nums',
            }}>
              {detail.scannedBags} / {detail.totalBags}
              <Box component="span" sx={{ color: C.textMuted, ml: 0.5 }}>
                ({detail.bagPct}%)
              </Box>
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate" value={detail.bagPct}
            sx={{
              height: 4, borderRadius: 2, mb: 1,
              backgroundColor: 'rgba(255,255,255,0.06)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: detail.bagPct < 100 ? C.accent : '#4CAF50',
              },
            }}
          />
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 0.6, mb: 1 }}>
            {detail.bins.map(b => {
              const pct = b.total > 0 ? (b.scanned / b.total) * 100 : 0;
              return (
                <Box key={b.letter} sx={{
                  textAlign: 'center', px: 0.4, py: 0.5,
                  backgroundColor: 'rgba(255,255,255,0.02)',
                  border: `1px solid ${C.border}`, borderRadius: 0.4,
                }}>
                  <Typography sx={{
                    fontSize: '0.55rem', color: C.textMuted, fontWeight: 700, letterSpacing: 0.5,
                    textTransform: 'uppercase',
                  }}>{b.letter}</Typography>
                  <Typography sx={{
                    fontSize: '0.78rem', color: C.textPrimary, fontWeight: 700,
                    fontVariantNumeric: 'tabular-nums', lineHeight: 1.1,
                  }}>
                    {b.scanned}
                    <Box component="span" sx={{ color: C.textMuted, fontWeight: 400, fontSize: '0.55rem' }}>
                      /{b.total}
                    </Box>
                  </Typography>
                  <Box sx={{
                    height: 2, mt: 0.4, borderRadius: 1,
                    backgroundColor: 'rgba(255,255,255,0.06)',
                    overflow: 'hidden',
                  }}>
                    <Box sx={{
                      height: '100%', width: `${pct}%`,
                      backgroundColor: pct < 100 ? C.accent : '#4CAF50',
                    }} />
                  </Box>
                </Box>
              );
            })}
          </Box>
          {/* Upload — appears in the same panel under the bins. Only
              meaningful when the outbound has bag count to track; for
              originators / terminators uploadTotal is 0 and we hide it. */}
          {detail.uploadTotal > 0 && (() => {
            const uploadPct = Math.round((detail.uploadLoaded / detail.uploadTotal) * 100);
            const done = uploadPct >= 100;
            return (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5, mt: 0.5 }}>
                  <SectionLabel icon={FlightTakeoffIcon} color={C.textSecondary}>Upload</SectionLabel>
                  <Typography sx={{
                    fontSize: '0.7rem', color: done ? '#4CAF50' : '#00BCD4',
                    fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                  }}>
                    {detail.uploadLoaded} / {detail.uploadTotal}
                    <Box component="span" sx={{ color: C.textMuted, ml: 0.5 }}>
                      ({uploadPct}%)
                    </Box>
                    {detail.hotPending > 0 && (
                      <Box component="span" sx={{
                        ml: 0.75, color: '#FF0000', fontWeight: 800,
                        letterSpacing: 0.4, textTransform: 'uppercase',
                      }}>
                        {detail.hotPending} hot
                      </Box>
                    )}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate" value={uploadPct}
                  sx={{
                    height: 4, borderRadius: 2,
                    backgroundColor: 'rgba(255,255,255,0.06)',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: done ? '#4CAF50' : '#00BCD4',
                    },
                  }}
                />
              </>
            );
          })()}
        </Box>

        {/* Connections */}
        <Box sx={{ px: 1.5, py: 1.25, borderBottom: `1px solid ${C.border}` }}>
          <SectionLabel icon={FlightTakeoffIcon} color={C.textSecondary}>
            Connections ({detail.connections.length})
          </SectionLabel>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {detail.connections.map((c, i) => {
              const accent = c.isHot ? '#FF0000' : c.isCold ? '#2196F3' : '#94A3B8';
              return (
                <Box key={i}
                  onClick={(e) => { e.stopPropagation(); setSelected({ kind: 'flight', id: c.flightNumber }); }}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 0.75,
                    px: 0.85, py: 0.5,
                    backgroundColor: c.isHot ? 'rgba(255, 0, 0, 0.06)' : 'rgba(255,255,255,0.025)',
                    border: `1px solid ${c.isHot ? '#FF444455' : C.border}`,
                    borderLeft: `3px solid ${accent}`,
                    borderRadius: 0.4,
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: c.isHot ? 'rgba(255, 0, 0, 0.12)' : 'rgba(255,255,255,0.05)' },
                  }}
                >
                  {c.isHot && <WhatshotIcon sx={{ fontSize: 14, color: '#FF0000' }} />}
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                      <Typography sx={{
                        fontSize: '0.78rem', color: C.textPrimary, fontWeight: 700,
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                      }}>
                        {c.flightNumber.replace(/^WN\s*/, '')}
                      </Typography>
                      <ArrowForwardIcon sx={{ fontSize: 10, color: C.textMuted }} />
                      <Typography sx={{ fontSize: '0.7rem', color: C.textSecondary, fontWeight: 600 }}>
                        {c.destination}
                      </Typography>
                      <Typography sx={{ fontSize: '0.6rem', color: C.textMuted }}>· {c.gate}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.15 }}>
                      <AccessTimeIcon sx={{ fontSize: 10, color: accent }} />
                      <Typography sx={{ fontSize: '0.62rem', color: accent, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                        {c.connectionMin}m
                      </Typography>
                      <Typography sx={{ fontSize: '0.62rem', color: C.textMuted }}>
                        · {c.bags} bag{c.bags === 1 ? '' : 's'}
                      </Typography>
                      <Box sx={{ flex: 1 }} />
                      <Tooltip title="Predicted probability the bags make this connection">
                        <Typography sx={{
                          fontSize: '0.62rem', fontWeight: 700,
                          color: c.makeConfidence >= 90 ? '#4CAF50'
                                : c.makeConfidence >= 75 ? '#F9B612'
                                : '#FF0000',
                          fontVariantNumeric: 'tabular-nums',
                        }}>
                          {c.makeConfidence}% make
                        </Typography>
                      </Tooltip>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Notes (annotations) */}
        <Box sx={{ px: 1.5, py: 1.25, borderBottom: `1px solid ${C.border}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.6 }}>
            <SectionLabel icon={StickyNote2Icon} color={C.textSecondary}>
              Notes ({notes.length})
            </SectionLabel>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); setAdding(v => !v); }}
              sx={{
                color: adding ? C.accent : C.textMuted,
                p: 0.25,
                '&:hover': { color: C.accent },
              }}
            >
              <AddIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
          {adding && (
            <Box sx={{ display: 'flex', gap: 0.5, mb: notes.length > 0 ? 0.75 : 0 }}>
              <TextField
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (draft.trim()) {
                      addAnnotation(entity, draft);
                      setDraft('');
                      setAdding(false);
                    }
                  }
                  if (e.key === 'Escape') { setAdding(false); setDraft(''); }
                }}
                onClick={(e) => e.stopPropagation()}
                placeholder="Note for this gate (Enter to save)…"
                size="small" fullWidth autoFocus
                multiline maxRows={3}
                sx={{
                  '& .MuiInputBase-root': {
                    backgroundColor: 'rgba(0,0,0,0.35)', color: C.textPrimary,
                    fontSize: '0.72rem', py: 0.5, px: 1,
                  },
                  '& fieldset': { borderColor: C.border },
                  '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: C.accent },
                }}
              />
            </Box>
          )}
          {notes.length === 0 && !adding ? (
            <Typography sx={{ fontSize: '0.65rem', color: C.textMuted, fontStyle: 'italic' }}>
              No notes. Click + to drop one.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {notes.map(n => (
                <Box key={n.id} sx={{
                  display: 'flex', alignItems: 'flex-start', gap: 0.5,
                  px: 0.85, py: 0.5,
                  backgroundColor: 'rgba(249, 182, 18, 0.06)',
                  borderLeft: `3px solid ${C.accent}`,
                  borderRadius: 0.4,
                }}>
                  <Typography sx={{ fontSize: '0.7rem', color: C.textPrimary, flex: 1, lineHeight: 1.3 }}>
                    {n.text}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); removeAnnotation(entity, n.id); }}
                    sx={{ color: C.textMuted, p: 0.2, '&:hover': { color: '#f44336' } }}
                  >
                    <CloseIcon sx={{ fontSize: 11 }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        {/* Up next */}
        <Box sx={{ px: 1.5, py: 1.25 }}>
          <SectionLabel icon={FlightLandIcon} color={C.textSecondary}>
            Up Next at {detail.gateId}
          </SectionLabel>
          {detail.nextInbound ? (
            <Box sx={{
              px: 0.85, py: 0.75,
              backgroundColor: 'rgba(255,255,255,0.025)',
              border: `1px solid ${C.border}`, borderRadius: 0.5,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                <Typography sx={{
                  fontSize: '0.85rem', color: C.textPrimary, fontWeight: 700,
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                }}>{detail.nextInbound.flightNumber}</Typography>
                <Typography sx={{ fontSize: '0.7rem', color: C.textSecondary }}>
                  from {detail.nextInbound.origin}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.4 }}>
                <Box sx={{
                  width: 6, height: 6, borderRadius: '50%',
                  backgroundColor: PHASE_LABELS[detail.nextInbound.phase].color,
                  boxShadow: `0 0 6px ${PHASE_LABELS[detail.nextInbound.phase].color}`,
                }} />
                <Typography sx={{
                  fontSize: '0.65rem', color: PHASE_LABELS[detail.nextInbound.phase].color,
                  fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
                }}>
                  {PHASE_LABELS[detail.nextInbound.phase].label}
                </Typography>
                <Typography sx={{ fontSize: '0.65rem', color: C.textMuted }}>
                  · in {detail.nextInbound.minutesAway} min
                </Typography>
              </Box>
            </Box>
          ) : (
            <Typography sx={{ fontSize: '0.7rem', color: C.textMuted, fontStyle: 'italic' }}>
              Nothing else scheduled at this gate.
            </Typography>
          )}
        </Box>
      </Box>
    </Paper>
  );
}

function SectionLabel({ icon: Icon, color, children }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.6 }}>
      <Icon sx={{ fontSize: 12, color }} />
      <Typography sx={{
        fontSize: '0.55rem', color: C.textMuted, fontWeight: 700,
        letterSpacing: 0.7, textTransform: 'uppercase',
      }}>{children}</Typography>
    </Box>
  );
}

export default GateDetailCard;
export { resolveDetail };
